import { CheckIcon, SparklesIcon, FireIcon, BoltIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { userPlanList } from "../../services/planServices";
import { PulseLoader } from "react-spinners";
import PermissionsModal from "units/admin/plans/myPlanModal/PermissionsModal";
import { useTranslation } from "react-i18next";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const PermCategory = {
  PAGE: "page",
  API: "api",
  AI_MODEL: "ai",
  EMBEDDER: "textEmbder",
  CHART: "analysticChart",
  PLATFORM: "platform",
  TOOL: "ai-tool",
};

const groupPermissionsByCategory = (permissions = []) => {
  return permissions.reduce((acc, item) => {
    if (!item.isAvailable) return acc;
    const category = item.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push({
      id: item.id,
      name: item.name,
      label: item.persianName,
      logo: item.logo,
    });
    return acc;
  }, {});
};

/* ================================================
   AMBIENT BACKGROUND
   ================================================ */
const AmbientBackground = () => {
  const [stars, setStars] = useState([]);

  useEffect(() => {
    const generatedStars = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: Math.random() * 2.5 + 1,
      delay: Math.random() * 5,
      duration: Math.random() * 3 + 2,
      opacity: Math.random() * 0.6 + 0.3,
    }));
    setStars(generatedStars);
  }, []);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      {/* base gradient */}
      <div className="absolute inset-0
                            bg-gradient-to-br from-white via-teal-50/50 to-amber-50/40
                            dark:from-slate-950 dark:via-slate-900 dark:to-teal-950/60" />

      {/* big teal orb */}
      <div className="absolute -top-40 -right-40 w-[32rem] h-[32rem] rounded-full
                            bg-gradient-to-br from-teal-300/40 to-teal-500/20
                            dark:from-teal-600/25 dark:to-teal-900/10
                            blur-3xl animate-[drift_20s_ease-in-out_infinite]" />

      {/* big gold orb */}
      <div className="absolute -bottom-40 -left-40 w-[32rem] h-[32rem] rounded-full
                            bg-gradient-to-br from-amber-300/40 to-amber-500/20
                            dark:from-amber-500/15 dark:to-amber-900/10
                            blur-3xl animate-[drift_24s_ease-in-out_infinite_reverse]" />

      {/* center subtle orb */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full
                            bg-gradient-to-br from-teal-200/20 via-transparent to-amber-200/20
                            dark:from-teal-800/10 dark:to-amber-800/10
                            blur-3xl" />

      {/* grid */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #0d9488 1px, transparent 1px), linear-gradient(to bottom, #0d9488 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      {/* stars */}
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full
                               bg-gradient-to-br from-amber-400 to-teal-400
                               dark:from-amber-300 dark:to-teal-300"
          style={{
            top: `${star.top}%`,
            left: `${star.left}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
            boxShadow: `0 0 ${star.size * 3}px rgba(212, 175, 55, 0.5)`,
          }}
        />
      ))}
    </div>
  );
};

/* ================================================
   FEATURE ROW
   ================================================ */
const FeatureRow = ({ children, accent = "teal" }) => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";
  const accentClasses =
    accent === "gold"
      ? "from-amber-400 to-amber-600 shadow-amber-500/40"
      : "from-teal-500 to-teal-700 shadow-teal-500/40";

  return (
    <li className="flex items-start gap-3">
      <div className={`w-5 h-5 rounded-full flex-shrink-0 mt-0.5
                             bg-gradient-to-br ${accentClasses}
                             flex items-center justify-center
                             shadow-md`}>
        <CheckIcon className="h-3 w-3 text-white" aria-hidden="true" />
      </div>
      <span className={`text-[13px] sm:text-sm font-medium leading-5 ${isEnglish && 'font-sans'}`}>
        {children.toLocaleString('en-US')}
      </span>
    </li>
  );
};

/* ================================================
   MAIN COMPONENT
   ================================================ */
export default function Plans({ plans, setPlans }) {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isPermModalOpen, setIsPermModalOpen] = useState(false);
  const [activePermissions, setActivePermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state.auth.accessToken);

  const handleSelectPlan = (plan) => {
    navigate("/cart", { state: { selectedPlan: plan } });
  };

  useEffect(() => {
    const allPlans = async () => {
      setLoading(true);
      try {
        const res = await userPlanList(accessToken);
        if (res?.ok) setPlans(res.data);
      } catch (error) {
        console.log("خطا در دریافت اطلاعات:", error);
      } finally {
        setLoading(false);
      }
    };
    allPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const sortedData = useMemo(() => {
    return [...plans].sort((a, b) => a.tokens - b.tokens);
  }, [plans]);

  const CATEGORY_LABELS = {
    analysticChart: t("plans.categoryLabels.analysticChart"),
    ai: t("plans.categoryLabels.ai"),
    textEmbder: t("plans.categoryLabels.textEmbder"),
    platform: t("plans.categoryLabels.platform"),
    "ai-tool": t("plans.categoryLabels.ai-tool"),
    page: t("plans.categoryLabels.page"),
  };

  return (
    <div className="relative z-0 min-h-screen pb-20 overflow-x-hidden">
      <AmbientBackground />

      <div className="relative z-10 px-4 sm:px-6 md:px-8 py-10 sm:py-14">


        {/* ==================== LOADING SKELETON ==================== */}
        {loading && sortedData.length === 0 ? (
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i}
                className="h-[500px] rounded-3xl animate-pulse
                                           bg-white/40 dark:bg-slate-800/40
                                           ring-1 ring-slate-200/60 dark:ring-white/5" />
            ))}
          </div>
        ) : (
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {sortedData.map((tier, index) => {
              const permissionsByCategory = tier.permissions
                ? groupPermissionsByCategory(tier.permissions)
                : {};

              const hasDiscount =
                typeof tier?.offPercentage === "number" && tier.offPercentage > 0;

              // طرح میانی = پیشنهاد ویژه
              const isFeatured =
                !tier.isBase &&
                index === Math.floor(sortedData.filter(p => !p.isBase).length / 2) + (sortedData.findIndex(p => !p.isBase));

              const discountedPrice = hasDiscount
                ? tier.price * (1 - tier.offPercentage / 100)
                : tier.price;

              return (
                <div
                  key={tier.id}
                  className={`group relative rounded-3xl
                                                transition-all duration-500
                                                hover:-translate-y-2
                                                ${isFeatured ? "lg:scale-[1.04] lg:z-10" : ""}`}
                  style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both` }}
                >
                  {/* glow behind featured card */}
                  {isFeatured && (
                    <div className="absolute -inset-1 rounded-[2rem] -z-10
                                                        bg-gradient-to-br from-teal-500/40 via-amber-400/30 to-teal-500/40
                                                        blur-2xl opacity-70
                                                        animate-pulse" />
                  )}

                  <div
                    className={`relative h-full rounded-3xl overflow-hidden
                                                    backdrop-blur-2xl
                                                    transition-all duration-500
                                                    ${isFeatured
                        ? `bg-gradient-to-br from-teal-800 via-teal-900 to-slate-900
                                                           dark:from-teal-800 dark:via-teal-900 dark:to-slate-950
                                                           ring-2 ring-amber-400/50
                                                           shadow-[0_30px_80px_-20px_rgba(13,148,136,0.5)]`
                        : `bg-white/75 dark:bg-slate-900/60
                                                           ring-1 ring-slate-200/70 dark:ring-white/10
                                                           shadow-[0_20px_60px_-25px_rgba(13,148,136,0.30)]
                                                           dark:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.8)]
                                                           hover:shadow-[0_30px_80px_-25px_rgba(13,148,136,0.45)]
                                                           hover:ring-teal-300/60 dark:hover:ring-teal-500/30`
                      }`}
                  >
                    {/* gold top line on featured */}
                    {isFeatured && (
                      <div className="absolute top-0 inset-x-0 h-[3px]
                                                            bg-gradient-to-r from-transparent via-amber-400 to-transparent
                                                            bg-[length:200%_100%]
                                                            animate-[shimmer_3s_linear_infinite]" />
                    )}

                    {/* featured badge */}
                    {isFeatured && (
                      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
                        <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full
                                                                bg-gradient-to-r from-amber-400 to-amber-500
                                                                text-white text-[11px] font-black tracking-wider
                                                                shadow-lg shadow-amber-500/50
                                                                ring-2 ring-white/30">
                          <FireIcon className="w-3.5 h-3.5" />
                          {isEnglish ? "POPULAR" : "محبوب"}
                        </div>
                      </div>
                    )}

                    {/* hover shine */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100
                                                        bg-gradient-to-br from-teal-400/5 via-transparent to-amber-400/5
                                                        transition-opacity duration-500 pointer-events-none" />

                    <div className={`relative p-6 sm:p-7 ${isFeatured ? "pt-12" : ""}`}>

                      {/* ============ Plan name ============ */}
                      <div className="flex items-center justify-between mb-1">
                        <h3
                          className={`text-xl sm:text-2xl font-black tracking-tight
                                                                ${isFeatured
                              ? "text-white"
                              : "text-teal-800 dark:text-teal-300"}`}
                          id={tier.id}
                        >
                          {tier.name}
                        </h3>

                        {hasDiscount && (
                          <div className="relative">
                            <div className="absolute inset-0 rounded-xl
                                                                        bg-red-500/50 blur-md" />
                            <div className={`relative text-white text-[11px] font-black
                                                                        px-2.5 py-1 rounded-xl
                                                                        bg-gradient-to-br from-red-500 to-rose-600
                                                                        shadow-lg
                                                                        animate-[pulseGold_2s_ease-in-out_infinite] ${isEnglish && 'font-sans'}`}>
                              {tier.offPercentage}٪
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ============ Subtitle ============ */}
                      <p className={`text-[12px] mb-5
                                                           ${isFeatured
                          ? "text-teal-100/70"
                          : "text-slate-400 dark:text-slate-500"}`}>
                        {isEnglish
                          ? "Perfect for your business needs"
                          : "مناسب برای نیازهای شما"}
                      </p>

                      {/* ============ Divider ============ */}
                      <div className={`h-px mb-5
                                                            ${isFeatured
                          ? "bg-gradient-to-r from-transparent via-amber-400/40 to-transparent"
                          : "bg-gradient-to-r from-transparent via-teal-400/40 to-transparent"}`} />

                      {/* ============ Price ============ */}
                      <div className="mb-6">
                        <div className="flex items-end gap-2 flex-wrap">
                          {/* discounted price */}
                          <div className="flex items-baseline gap-1">
                            <span className={`text-3xl sm:text-4xl font-black tabular-nums tracking-tight
                                                                          ${isFeatured
                                ? "text-white"
                                : "text-teal-800 dark:text-white"}
                                                                ${isEnglish && 'font-sans'}`}>
                              {Math.round(discountedPrice).toLocaleString("en-US")}
                            </span>
                            <span className={`text-sm font-bold
                                                                          ${isFeatured
                                ? "text-amber-300"
                                : "text-teal-600 dark:text-teal-400"}`}>
                              {t("plans.currency")}
                            </span>
                          </div>

                          {/* original price struck-through */}
                          {hasDiscount && (
                            <span className={`text-sm line-through tabular-nums pb-1
                                                                          ${isFeatured
                                ? "text-teal-200/50"
                                : "text-slate-400 dark:text-slate-500"}
                                                                ${isEnglish && 'font-sans'}`}>
                              {tier.price.toLocaleString("en-US")}
                            </span>
                          )}
                        </div>

                        <p className={`text-[11px] mt-1.5
                                                               ${isFeatured
                            ? "text-teal-100/60"
                            : "text-slate-400 dark:text-slate-500"}
                                                        ${isEnglish && 'font-sans'}`}>
                          {isEnglish ? "per" : "برای"}{" "}
                          {tier?.duration === -1
                            ? t("plans.unlimited")
                            : tier.duration.toLocaleString("en-US")}{" "}
                          {t("plans.day")}
                        </p>
                      </div>

                      {/* ============ Features ============ */}
                      <ul
                        role="list"
                        className={`space-y-3.5
                                                            ${isFeatured
                            ? "text-teal-50"
                            : "text-slate-700 dark:text-slate-200"}`}
                      >
                        <FeatureRow accent={isFeatured ? "gold" : "teal"}>
                          {t("plans.tokens", { count: tier.tokens.toLocaleString("en-US") })}
                        </FeatureRow>

                        <FeatureRow accent={isFeatured ? "gold" : "teal"}>
                          {t("plans.filesCount", {
                            count: tier.files === -1
                              ? t("plans.unlimited")
                              : tier.files.toLocaleString("en-US"),
                          })}
                        </FeatureRow>

                        <FeatureRow accent={isFeatured ? "gold" : "teal"}>
                          {t("plans.fileSize", {
                            count: tier.fileSize === -1
                              ? t("plans.unlimited")
                              : tier.fileSize.toLocaleString("en-US"),
                          })}
                        </FeatureRow>

                        <FeatureRow accent={isFeatured ? "gold" : "teal"}>
                          {t("plans.userLimitCount", { count: tier.userLimit.toLocaleString("en-US") })}
                        </FeatureRow>

                        <FeatureRow accent={isFeatured ? "gold" : "teal"}>
                          {t("plans.platformPercent", {
                            count: tier.platformPercent.toLocaleString("en-US"),
                          })}
                        </FeatureRow>

                        <li className="flex items-start gap-3 pt-2">
                          <div className={`w-5 h-5 rounded-full flex-shrink-0 mt-0.5
                                                                     bg-gradient-to-br from-amber-400 to-amber-600
                                                                     shadow-md shadow-amber-500/40
                                                                     flex items-center justify-center`}>
                            <BoltIcon className="h-3 w-3 text-white" />
                          </div>

                          {Object.keys(permissionsByCategory).length > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                setActivePermissions(permissionsByCategory);
                                setIsPermModalOpen(true);
                              }}
                              className={`text-[13px] font-bold underline decoration-2 underline-offset-4
                                                                        transition-all duration-300 text-right
                                                                        ${isFeatured
                                  ? "text-amber-300 hover:text-amber-200 decoration-amber-400/60"
                                  : "text-teal-700 dark:text-teal-300 hover:text-teal-600 dark:hover:text-teal-200 decoration-teal-500/50 dark:decoration-teal-300/50"}`}
                            >
                              {t("plans.hasSpecialFeatures")}
                            </button>
                          ) : (
                            <span className={`text-[13px]
                                                                          ${isFeatured
                                ? "text-teal-100/60"
                                : "text-slate-400 dark:text-slate-500"}`}>
                              {t("plans.noSpecialFeatures")}
                            </span>
                          )}
                        </li>
                      </ul>

                      {/* ============ CTA ============ */}
                      {!tier.isBase && (
                        <button
                          disabled={loading}
                          onClick={() => handleSelectPlan(tier)}
                          aria-describedby={tier.id}
                          className={`group/btn relative mt-7 w-full
                                                                rounded-2xl px-6 py-3.5
                                                                text-white font-bold text-[15px]
                                                                transition-all duration-500
                                                                overflow-hidden
                                                                active:scale-[0.98]
                                                                disabled:opacity-60 disabled:cursor-not-allowed
                                                                ${isFeatured
                              ? `bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400
                                                                       bg-[length:200%_100%] hover:bg-[position:100%_0]
                                                                       shadow-[0_10px_30px_-8px_rgba(251,191,36,0.6)]
                                                                       hover:shadow-[0_15px_40px_-8px_rgba(251,191,36,0.8)]
                                                                       hover:scale-[1.02]`
                              : `bg-gradient-to-r from-teal-700 via-teal-600 to-amber-600
                                                                       bg-[length:200%_100%] hover:bg-[position:100%_0]
                                                                       shadow-[0_10px_30px_-8px_rgba(13,148,136,0.5)]
                                                                       hover:shadow-[0_15px_40px_-8px_rgba(13,148,136,0.7)]
                                                                       hover:scale-[1.02]`
                            }`}
                        >
                          {/* sheen */}
                          <span className="absolute inset-0
                                                                     bg-gradient-to-r from-transparent via-white/40 to-transparent
                                                                     -translate-x-full group-hover/btn:translate-x-full
                                                                     transition-transform duration-1000" />

                          <div className="relative flex items-center justify-center gap-2">
                            {loading ? (
                              <PulseLoader color="#ffffff" size={9} />
                            ) : (
                              <>
                                <span>{t("plans.selectPlan")}</span>
                                <svg
                                  className="w-4 h-4 group-hover/btn:-translate-x-1 transition-transform duration-300"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                              </>
                            )}
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PermissionsModal
        isOpen={isPermModalOpen}
        onClose={() => setIsPermModalOpen(false)}
        permissionsByCategory={activePermissions}
        categoryLabels={CATEGORY_LABELS}
      />

      <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes shimmer {
                    0%   { background-position: 0% 50%; }
                    100% { background-position: 200% 50%; }
                }
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50%      { opacity: 1; transform: scale(1.3); }
                }
                @keyframes drift {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    50%      { transform: translate(30px, -20px) scale(1.08); }
                }
                @keyframes pulseGold {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.6); }
                    50%      { box-shadow: 0 0 0 10px rgba(251, 191, 36, 0); }
                }
            `}</style>
    </div>
  );
}